import { Collapse, CollapseProps, Select, Skeleton } from "antd"
import { H5 } from "components"
import { useState } from "react"
import calenderPlus from "assets/svg/CalendarPlus.svg"
import upIcon from "assets/svg/chevron-up.svg"
import downIcon from "assets/svg/chevron-down.svg"
import EmptyImg from "../../../../assets/illustrations/timesheet-empty-img.svg"
import { useGet } from "utils/useFetch"
import { IOvertimeRes } from "../interface/shiftData"
import { formatGroupLabel, formatDay, formatTime, sortByDate } from "../utils"
import { OvertimeCollapseRow } from "./OvertimeCollapseRow"
import { OvertimeDetailsRow } from "./OvertimeDetailsRow"
import OvertimeEntryModal from "./OvertimeEntryModal"

const OvertimeSubmission = () => {
  const [activeOvertimeCollapseKeys, setActiveOvertimeCollapseKeys] =
    useState("")
  const [showOvertimeEntry, setShowOvertimeEntry] = useState(false)
  const [groupBy, setGroupBy] = useState<"day" | "week" | "month">("week")
  const [reasonEntry, setReasonEntry] = useState<{
    date: string
    overtimeStart: string
    overtimeEnd: string
    id: number | null
  } | null>(null)

  const startDate = "2020-01-01"
  const endDate = new Date().toISOString().split("T")[0]

  const queryKey = `/v1/time-attendance/employee/overtime?startDate=${startDate}&endDate=${endDate}&limit=1000&groupBy=${groupBy}`

  const { data: overtimeData, isLoading } = useGet<IOvertimeRes>({
    url: queryKey,
    instanceKey: "ewaAxiosInstance",
    keepPreviousData: true,
  })
  const overtimeRecords = overtimeData?.data.docs
  const noOvertimeRecords =
    !isLoading && overtimeRecords && Object.keys(overtimeRecords).length === 0

  const overtimeSubmissions: CollapseProps["items"] = Object.entries(
    overtimeRecords ?? {}
  )
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([key, records]) => ({
      key: key,
      label: (
        <span className="flex justify-between items-center gap-5">
          <p>{formatGroupLabel(key, groupBy)}</p>
          <button className="flex items-center gap-2">
            <span>Collapse</span>
            {activeOvertimeCollapseKeys === key ? (
              <img
                src={upIcon}
                width={20}
                height={20}
              />
            ) : (
              <img
                src={downIcon}
                width={20}
                height={20}
              />
            )}
          </button>
        </span>
      ),
      children: (
        <div className="border-t border-t-gray-300">
          <div className="hidden sm:block">
            {records.length === 0 ? (
              <div className="mx-auto">
                <img
                  src={EmptyImg}
                  className="w-full object-contain"
                />
              </div>
            ) : (
              sortByDate(records, "date").map((item) => (
                <OvertimeDetailsRow
                  key={item.id}
                  day={formatDay(item.date)}
                  date={item.date}
                  overtimeStart={formatTime(item.overtimeStartTime)}
                  overtimeEnd={formatTime(item.overtimeEndTime)}
                  reason={item.reason}
                  status={item.status}
                  id={item.id}
                  onAddReason={(entry) => setReasonEntry(entry)}
                />
              ))
            )}
          </div>
          <div className="block sm:hidden">
            <OvertimeCollapseRow
              setReasonEntry={setReasonEntry}
              records={records}
              groupBy={groupBy}
              onAddReason={(entry) => setReasonEntry(entry)}
            />
          </div>
        </div>
      ),
    }))

  return (
    <div
      className="bg-white p-4 my-2"
      style={{ fontFamily: '"Circular Std", sans-serif' }}
    >
      <div className="block lg:flex items-center justify-between mb-2 sm:mb-5">
        <div className="mb-3">
          <H5 className="font-circular text-padeBlack font-semibold shrink-0 text:sm sm:text-base">
            Overtime submissions
          </H5>
          <p className="text-[#62748E] text-sm">
            Enter overtime hours worked outside your regular shift for review
            and approval.
          </p>
        </div>
        <div className="flex items-center justify-center sm:justify-end gap-2">
          <div className="relative">
            <Select
              value={groupBy}
              onChange={(value) => {
                setGroupBy(value)
                setActiveOvertimeCollapseKeys("")
              }}
              suffixIcon={null}
              bordered={false}
              options={[
                { value: "day", label: "Group in days" },
                { value: "week", label: "Group in weeks" },
                { value: "month", label: "Group in months" },
              ]}
              className="border border-[#CAD5E2] max-w-fit rounded-md py-2 px-4 flex-1 mr-1 [&_.ant-select-selection-item]:font-bold text-neutral-500 bg-white"
            />
            <img
              className="absolute right-2 top-2 ml-1"
              src={downIcon}
              width={20}
              height={20}
            />
          </div>

          <div className="border-r h-8 border-[#CAD5E2]" />

          <button
            onClick={() => setShowOvertimeEntry(true)}
            className="w-fit flex flex-1 items-center gap-2 bg-white border justify-center border-[#CAD5E2] py-1 px-4 rounded-md"
          >
            <img
              src={calenderPlus}
              width={15}
              height={15}
            />
            <span className="text-sm text-padeBlack whitespace-nowrap">
              Submit new overtime
            </span>
          </button>
        </div>
      </div>

      <div className="border-none sm:border border-gray-300 rounded-t-lg overflow-hidden mt-2 sm:mt-5">
        <ul className="hidden sm:grid grid-cols-[100px_1fr_1fr_1fr_1fr_100px] gap-3 items-center bg-[#F8FAFC] border-b border-gray-300 py-3 px-2 text-slate-500 text-sm">
          <li>Day</li>
          <li>Overtime start</li>
          <li>Overtime end</li>
          <li>Overtime duration</li>
          <li>Reason</li>
          <li>Status</li>
        </ul>
        {isLoading ? (
          <Skeleton.Button
            active
            block
            style={{ height: 60, borderRadius: 6, marginTop: 2 }}
          />
        ) : noOvertimeRecords ? (
          <div className="mx-auto">
            <img
              src={EmptyImg}
              className="w-full object-contain"
            />
          </div>
        ) : (
          <Collapse
            style={{ background: "transparent" }}
            className="[&_.ant-collapse-item]:py-2 [&_.ant-collapse-item]:border-white [&_.ant-collapse-item]:sm:border-[#E2E8F0]"
            bordered={false}
            accordion
            activeKey={activeOvertimeCollapseKeys}
            items={overtimeSubmissions}
            expandIcon={() => null}
            onChange={(keys) => setActiveOvertimeCollapseKeys(keys.toString())}
          />
        )}
      </div>

      <OvertimeEntryModal
        open={showOvertimeEntry}
        onClose={() => setShowOvertimeEntry(false)}
        queryKey={queryKey}
        mode="new"
      />
      <OvertimeEntryModal
        open={!!reasonEntry}
        onClose={() => setReasonEntry(null)}
        queryKey={queryKey}
        mode="addReason"
        prefill={reasonEntry ?? undefined}
      />
    </div>
  )
}

export default OvertimeSubmission
